import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  PUSH_STATUS,
  disableWebPush,
  enableWebPush,
  getPushStatus,
  isPushEnabledLocally,
  listenForegroundMessages,
  registerPushServiceWorker,
  syncWebPushToken,
} from '../services/webPush';

/**
 * Signed-in web push: status, enable/disable, token refresh, foreground no-op listener.
 */
export default function useWebPush() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  const [status, setStatus] = useState(() => getPushStatus());
  const [enabledLocally, setEnabledLocally] = useState(() => isPushEnabledLocally());
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    setStatus(getPushStatus());
    setEnabledLocally(isPushEnabledLocally());
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!uid) return undefined;
    registerPushServiceWorker().catch(() => {});
    return undefined;
  }, [uid]);

  useEffect(() => {
    if (!uid || !isPushEnabledLocally()) return undefined;
    if (getPushStatus() !== PUSH_STATUS.GRANTED) return undefined;
    syncWebPushToken(uid).catch(() => {});
    return listenForegroundMessages();
  }, [uid]);

  const enable = useCallback(async () => {
    if (!uid || busy) return;
    setBusy(true);
    try {
      await enableWebPush(uid);
      refresh();
    } finally {
      setBusy(false);
    }
  }, [uid, busy, refresh]);

  const disable = useCallback(async () => {
    if (!uid || busy) return;
    setBusy(true);
    try {
      await disableWebPush(uid);
      refresh();
    } finally {
      setBusy(false);
    }
  }, [uid, busy, refresh]);

  const alertsOn = status === PUSH_STATUS.GRANTED && enabledLocally;

  return {
    status,
    enabledLocally,
    alertsOn,
    busy,
    enable,
    disable,
    refresh,
  };
}
